import React, { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Form,
  ListGroup,
  Modal,
  Spinner,
  Table,
} from "react-bootstrap";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast, { Toaster } from "react-hot-toast";
import { apiClient } from "../../../api/client";
import { useAuth } from "../../../app/providers/AuthProvider";
import { useMemberships } from "../../billing/hooks/useMemberships";
import { usePlanLimits } from "../hooks/usePlanLimits";

const INITIAL_NEW = { name: "", email: "", password: "" };

function useRegisterTeacher() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiClient.post("/api/v1/onboarding/teachers/", body).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["memberships", "TEACHER"] });
      qc.invalidateQueries({ queryKey: ["plan-limits"] });
    },
  });
}

// Establece exactamente los roles gestionables (TEACHER/STUDENT) de un miembro.
function useSetMemberRoles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ email, roles }) =>
      apiClient
        .post("/api/v1/memberships/set-roles/", { user_email: email, roles })
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["memberships", "TEACHER"] });
      qc.invalidateQueries({ queryKey: ["memberships", "STUDENT"] });
      qc.invalidateQueries({ queryKey: ["plan-limits"] });
    },
  });
}

function useUnlinkTeacher() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (membershipId) =>
      apiClient
        .patch(`/api/v1/memberships/${membershipId}/`, { is_active: false })
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["memberships", "TEACHER"] });
      qc.invalidateQueries({ queryKey: ["plan-limits"] });
    },
  });
}

// El propietario se activa a sí mismo como docente (rol dual OWNER + TEACHER).
function useEnableSelfTeaching() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiClient.post("/api/v1/onboarding/me/teaching/").then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["memberships", "TEACHER"] });
      qc.invalidateQueries({ queryKey: ["plan-limits"] });
    },
  });
}

function apiErr(err, fallback) {
  const data = err?.response?.data;
  return (
    data?.user_email?.[0] ??
    data?.roles?.[0] ??
    data?.email?.[0] ??
    data?.message ??
    data?.detail ??
    fallback
  );
}

function DocentesPage() {
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState(INITIAL_NEW);

  // Promote (asignar docente a usuario existente) — con typeahead.
  const [showPromote, setShowPromote] = useState(false);
  const [promoteQuery, setPromoteQuery] = useState("");
  const [promoteSelected, setPromoteSelected] = useState(null); // { id, email, name }
  const [promoteKeepStudent, setPromoteKeepStudent] = useState(true);

  // Edit (cambiar rol de un docente).
  const [editing, setEditing] = useState(null); // teacher membership
  const [editKeepStudent, setEditKeepStudent] = useState(false);

  const { roles, addRole } = useAuth();
  const ownerIsTeacher = (roles ?? []).includes("TEACHER");

  const [showLimitAlert, setShowLimitAlert] = useState(false);
  const { data: limits } = usePlanLimits();
  const teacherLimit = limits?.teachers;
  const atTeacherLimit = teacherLimit?.allowed === false;

  const { data: teachers, isLoading, isError } = useMemberships("TEACHER");
  const { data: students } = useMemberships("STUDENT");
  const registerMutation = useRegisterTeacher();
  const setRolesMutation = useSetMemberRoles();
  const unlinkMutation = useUnlinkTeacher();
  const enableSelfMutation = useEnableSelfTeaching();

  // Bloquea agregar docentes cuando se alcanza el límite del plan (el backend
  // también lo enforce con 402; esto evita el intento fallido y lo explica).
  const guardLimit = (proceed) => {
    if (atTeacherLimit) {
      setShowLimitAlert(true);
      return;
    }
    setShowLimitAlert(false);
    proceed();
  };

  const handleEnableSelf = () => {
    if (atTeacherLimit) {
      setShowLimitAlert(true);
      return;
    }
    enableSelfMutation.mutate(undefined, {
      onSuccess: (data) => {
        (data?.roles ?? ["TEACHER"]).forEach(addRole);
        toast.success(
          "Ahora también eres docente. Usa «Ver como» en la barra superior para cambiar a modo docente."
        );
      },
      onError: (err) =>
        toast.error(apiErr(err, "No se pudo activarte como docente.")),
    });
  };

  // ¿Qué usuarios ya son docentes / estudiantes activos? (para dual + candidatos)
  const teacherUserIds = useMemo(
    () => new Set((teachers ?? []).map((m) => m.user?.id)),
    [teachers]
  );
  const studentUserIds = useMemo(
    () => new Set((students ?? []).map((m) => m.user?.id)),
    [students]
  );

  // Candidatos a promover: estudiantes que aún no son docentes, filtrados por texto.
  const promoteCandidates = useMemo(() => {
    const q = promoteQuery.trim().toLowerCase();
    return (students ?? [])
      .filter((s) => !teacherUserIds.has(s.user?.id))
      .map((s) => ({
        id: s.user?.id,
        email: s.user?.email ?? "",
        name: s.user?.full_name ?? "",
      }))
      .filter(
        (c) =>
          !q ||
          c.name.toLowerCase().includes(q) ||
          c.email.toLowerCase().includes(q)
      )
      .slice(0, 8);
  }, [students, teacherUserIds, promoteQuery]);

  const setNewField = (field) => (e) =>
    setNewForm((prev) => ({ ...prev, [field]: e.target.value }));

  const resetPromote = () => {
    setShowPromote(false);
    setPromoteQuery("");
    setPromoteSelected(null);
    setPromoteKeepStudent(true);
  };

  const handleCreateNew = (e) => {
    e.preventDefault();
    registerMutation.mutate(newForm, {
      onSuccess: () => {
        toast.success(
          `Docente registrado. Se envió la contraseña temporal a ${newForm.email}.`
        );
        setShowNew(false);
        setNewForm(INITIAL_NEW);
      },
      onError: (err) => toast.error(apiErr(err, "Error al registrar el docente.")),
    });
  };

  const handlePromote = (e) => {
    e.preventDefault();
    if (!promoteSelected) return;
    const roles = promoteKeepStudent ? ["TEACHER", "STUDENT"] : ["TEACHER"];
    setRolesMutation.mutate(
      { email: promoteSelected.email, roles },
      {
        onSuccess: () => {
          toast.success(
            promoteKeepStudent
              ? "Usuario asignado como docente (mantiene su rol de estudiante)."
              : "Usuario convertido en docente (rol de estudiante desactivado)."
          );
          resetPromote();
        },
        onError: (err) =>
          toast.error(apiErr(err, "No se pudo asignar el rol.")),
      }
    );
  };

  const openEdit = (m) => {
    setEditing(m);
    setEditKeepStudent(studentUserIds.has(m.user?.id));
  };

  const handleSaveEdit = (e) => {
    e.preventDefault();
    if (!editing) return;
    const roles = editKeepStudent ? ["TEACHER", "STUDENT"] : ["TEACHER"];
    setRolesMutation.mutate(
      { email: editing.user?.email, roles },
      {
        onSuccess: () => {
          toast.success("Rol del docente actualizado.");
          setEditing(null);
        },
        onError: (err) =>
          toast.error(apiErr(err, "No se pudo actualizar el rol.")),
      }
    );
  };

  const handleUnlink = (m) => {
    const label = m.user?.full_name || m.user?.email || "este docente";
    if (!window.confirm(`¿Desvincular a ${label} como docente?`)) return;
    unlinkMutation.mutate(m.id, {
      onSuccess: () => toast.success("Docente desvinculado."),
      onError: () => toast.error("No se pudo desvincular al docente."),
    });
  };

  return (
    <>
      <Toaster position="top-right" />
      <div className="container-fluid py-3">
        <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
          <div>
            <h4 className="mb-0">Docentes</h4>
            {teacherLimit && (
              <small className={atTeacherLimit ? "text-danger" : "text-muted"}>
                {teacherLimit.used} / {teacherLimit.limit ?? "∞"} docente
                {teacherLimit.limit !== 1 ? "s" : ""}
              </small>
            )}
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <Button
              variant={atTeacherLimit ? "outline-secondary" : "outline-primary"}
              style={{ minHeight: 44 }}
              onClick={() => guardLimit(() => setShowPromote(true))}
            >
              Asignar usuario existente
            </Button>
            <Button
              variant={atTeacherLimit ? "outline-secondary" : "primary"}
              style={{ minHeight: 44 }}
              onClick={() => guardLimit(() => setShowNew(true))}
            >
              + Nuevo docente
            </Button>
          </div>
        </div>
        <p className="text-muted small mb-3">
          Registra un docente nuevo, o asigna el rol de docente a un usuario que
          ya existe en tu academia (p.ej. un estudiante que ahora también enseña)
          sin crear otra cuenta.
        </p>

        {showLimitAlert && teacherLimit && (
          <Alert
            variant="warning"
            dismissible
            onClose={() => setShowLimitAlert(false)}
            className="mb-3"
          >
            <Alert.Heading className="h6">Límite de docentes alcanzado</Alert.Heading>
            Tu plan permite hasta{" "}
            <strong>
              {teacherLimit.limit} docente{teacherLimit.limit !== 1 ? "s" : ""}
            </strong>
            .{" "}
            {teacherLimit.suggest_upgrade
              ? "Con el costo actual de bloques de capacidad, mejorar tu plan puede ser más económico."
              : "Mejora tu plan o contrata un bloque de capacidad para agregar más docentes."}
          </Alert>
        )}

        {!ownerIsTeacher && (
          <Alert
            variant="light"
            className="border d-flex flex-wrap justify-content-between align-items-center gap-2"
          >
            <span>
              <i className="mdi mdi-account-tie text-primary me-1" aria-hidden="true" />
              ¿Tú también dictas clases en tu academia? Actívate como docente sin
              crear otra cuenta.
            </span>
            <Button
              variant={atTeacherLimit ? "outline-secondary" : "outline-primary"}
              size="sm"
              style={{ minHeight: 44 }}
              onClick={handleEnableSelf}
              disabled={enableSelfMutation.isPending}
            >
              {enableSelfMutation.isPending ? (
                <Spinner animation="border" size="sm" />
              ) : (
                "Enseñar yo mismo"
              )}
            </Button>
          </Alert>
        )}

        {isLoading && (
          <div className="text-center py-5">
            <Spinner animation="border" size="sm" />
          </div>
        )}

        {isError && (
          <Alert variant="danger">No se pudieron cargar los docentes.</Alert>
        )}

        {!isLoading && !isError && (
          <>
            {/* Desktop table */}
            <div className="d-none d-sm-block">
              <Card>
                <div className="table-responsive">
                  <Table hover className="mb-0 align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Nombre</th>
                        <th>Correo</th>
                        <th>Rol</th>
                        <th>Estado</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(teachers ?? []).length === 0 ? (
                        <tr>
                          <td colSpan={5} className="text-center text-muted py-4">
                            No hay docentes registrados aún.
                          </td>
                        </tr>
                      ) : (
                        (teachers ?? []).map((m) => (
                          <tr key={m.id}>
                            <td>{m.user?.full_name || "—"}</td>
                            <td>{m.user?.email}</td>
                            <td>
                              {studentUserIds.has(m.user?.id) ? (
                                <Badge bg="info">Docente + Estudiante</Badge>
                              ) : (
                                <Badge bg="light" text="dark">Docente</Badge>
                              )}
                            </td>
                            <td>
                              <Badge bg={m.is_active ? "success" : "secondary"}>
                                {m.is_active ? "Activo" : "Inactivo"}
                              </Badge>
                            </td>
                            <td>
                              <div className="d-flex gap-2">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  style={{ minHeight: 44 }}
                                  onClick={() => openEdit(m)}
                                >
                                  Editar
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  style={{ minHeight: 44 }}
                                  onClick={() => handleUnlink(m)}
                                  disabled={unlinkMutation.isPending}
                                >
                                  Desvincular
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card>
            </div>

            {/* Mobile cards */}
            <div className="d-sm-none d-flex flex-column gap-3">
              {(teachers ?? []).length === 0 && (
                <p className="text-muted text-center py-3">
                  No hay docentes registrados aún.
                </p>
              )}
              {(teachers ?? []).map((m) => (
                <Card key={m.id}>
                  <Card.Body>
                    <div className="fw-medium">{m.user?.full_name || "—"}</div>
                    <div className="text-muted small">{m.user?.email}</div>
                    <div className="d-flex gap-2 mt-2 mb-2">
                      {studentUserIds.has(m.user?.id) ? (
                        <Badge bg="info">Docente + Estudiante</Badge>
                      ) : (
                        <Badge bg="light" text="dark">Docente</Badge>
                      )}
                      <Badge bg={m.is_active ? "success" : "secondary"}>
                        {m.is_active ? "Activo" : "Inactivo"}
                      </Badge>
                    </div>
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-primary"
                        className="flex-fill"
                        style={{ minHeight: 44 }}
                        onClick={() => openEdit(m)}
                      >
                        Editar
                      </Button>
                      <Button
                        variant="outline-danger"
                        className="flex-fill"
                        style={{ minHeight: 44 }}
                        onClick={() => handleUnlink(m)}
                        disabled={unlinkMutation.isPending}
                      >
                        Desvincular
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Modal: nuevo docente (cuenta nueva) */}
      <Modal show={showNew} onHide={() => setShowNew(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Registrar nuevo docente</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateNew}>
          <Modal.Body>
            <div className="d-flex flex-column gap-3">
              <Form.Group>
                <Form.Label className="fw-medium">
                  Nombre completo <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Carlos Ramírez"
                  value={newForm.name}
                  onChange={setNewField("name")}
                  required
                />
              </Form.Group>
              <Form.Group>
                <Form.Label className="fw-medium">
                  Correo electrónico <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="email"
                  placeholder="docente@ejemplo.com"
                  value={newForm.email}
                  onChange={setNewField("email")}
                  required
                />
              </Form.Group>
              <Form.Group>
                <Form.Label className="fw-medium">
                  Contraseña temporal <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Mínimo 8 caracteres"
                  value={newForm.password}
                  onChange={setNewField("password")}
                  minLength={8}
                  required
                />
                <Form.Text className="text-muted">
                  El docente recibirá la contraseña por correo y deberá cambiarla
                  en su primer ingreso.
                </Form.Text>
              </Form.Group>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              style={{ minHeight: 44 }}
              onClick={() => setShowNew(false)}
              disabled={registerMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              style={{ minHeight: 44 }}
              disabled={registerMutation.isPending}
            >
              {registerMutation.isPending ? (
                <Spinner animation="border" size="sm" />
              ) : (
                "Registrar docente"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal: asignar rol a usuario existente (typeahead) */}
      <Modal show={showPromote} onHide={resetPromote} centered>
        <Modal.Header closeButton>
          <Modal.Title>Asignar docente a usuario existente</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handlePromote}>
          <Modal.Body>
            <p className="text-muted small">
              Busca un usuario que ya pertenece a tu academia (por su nombre o
              correo). Conservará su cuenta y datos; solo se le asignará el rol de
              docente.
            </p>
            <Form.Group className="mb-2">
              <Form.Label className="fw-medium">Buscar usuario</Form.Label>
              <Form.Control
                type="text"
                placeholder="Escribe nombre o correo…"
                value={promoteQuery}
                onChange={(e) => {
                  setPromoteQuery(e.target.value);
                  setPromoteSelected(null);
                }}
                autoFocus
              />
            </Form.Group>

            {!promoteSelected && promoteQuery.trim() && (
              <ListGroup className="mb-3" style={{ maxHeight: 220, overflowY: "auto" }}>
                {promoteCandidates.length === 0 ? (
                  <ListGroup.Item className="text-muted small">
                    Sin coincidencias entre tus estudiantes.
                  </ListGroup.Item>
                ) : (
                  promoteCandidates.map((c) => (
                    <ListGroup.Item
                      key={c.id}
                      action
                      type="button"
                      onClick={() => {
                        setPromoteSelected(c);
                        setPromoteQuery(c.name || c.email);
                      }}
                    >
                      <div className="fw-medium">{c.name || c.email}</div>
                      {c.name && <div className="text-muted small">{c.email}</div>}
                    </ListGroup.Item>
                  ))
                )}
              </ListGroup>
            )}

            {promoteSelected && (
              <Alert variant="light" className="border py-2 d-flex justify-content-between align-items-center">
                <span>
                  <i className="mdi mdi-account-check text-success me-1" aria-hidden="true" />
                  {promoteSelected.name || promoteSelected.email}
                </span>
                <Button
                  variant="link"
                  size="sm"
                  className="p-0"
                  onClick={() => {
                    setPromoteSelected(null);
                    setPromoteQuery("");
                  }}
                >
                  Cambiar
                </Button>
              </Alert>
            )}

            <Form.Group>
              <Form.Label className="fw-medium">Rol resultante</Form.Label>
              <Form.Check
                type="radio"
                id="promote-dual"
                name="promote-mode"
                checked={promoteKeepStudent}
                onChange={() => setPromoteKeepStudent(true)}
                label={
                  <span>
                    <strong>Rol dual</strong> — sigue siendo estudiante y también
                    dicta clases.
                  </span>
                }
              />
              <Form.Check
                type="radio"
                id="promote-teacher-only"
                name="promote-mode"
                checked={!promoteKeepStudent}
                onChange={() => setPromoteKeepStudent(false)}
                label={
                  <span>
                    <strong>Solo docente</strong> — deja de ser estudiante (su
                    historial se conserva y es reversible).
                  </span>
                }
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              style={{ minHeight: 44 }}
              onClick={resetPromote}
              disabled={setRolesMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              style={{ minHeight: 44 }}
              disabled={setRolesMutation.isPending || !promoteSelected}
            >
              {setRolesMutation.isPending ? (
                <Spinner animation="border" size="sm" />
              ) : (
                "Asignar rol"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal: editar rol de un docente */}
      <Modal show={Boolean(editing)} onHide={() => setEditing(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            Editar rol — {editing?.user?.full_name || editing?.user?.email}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveEdit}>
          <Modal.Body>
            <p className="text-muted small">
              Este usuario es docente. Decide si además debe seguir (o volver a
              ser) estudiante de la academia.
            </p>
            <Form.Check
              type="switch"
              id="edit-keep-student"
              checked={editKeepStudent}
              onChange={(e) => setEditKeepStudent(e.target.checked)}
              label="También es estudiante (rol dual)"
            />
            <Form.Text className="text-muted">
              Al desactivarlo, deja de ser estudiante (su historial se conserva y
              se puede reactivar). Para quitarle el rol de docente, usa
              &quot;Desvincular&quot;.
            </Form.Text>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              style={{ minHeight: 44 }}
              onClick={() => setEditing(null)}
              disabled={setRolesMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              style={{ minHeight: 44 }}
              disabled={setRolesMutation.isPending}
            >
              {setRolesMutation.isPending ? (
                <Spinner animation="border" size="sm" />
              ) : (
                "Guardar"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
}

export { DocentesPage };
